/**
 * apiPlaygroundCatalog.js
 * ==================================================================
 * מקור האמת היחיד של סביבת הבדיקות.
 * כל הראוטינג מוגדר כאן מראש – המשתמש בממשק משנה רק את הטוקן,
 * את ה-workspaceId ואת הפרמטרים של הפעולה עצמה.
 * ==================================================================
 */

/* ==================================================================
 *  ★ כתובת השרת – המקום היחיד לשינוי ★
 * ==================================================================
 * זו הכתובת שאליה נשלחות כל הבקשות בסביבת הבדיקות.
 * אפשר לקבע כתובת אחת:
 *     export const BASE_URL = 'https://ivrapi.clickcall.co.il'
 * או להשאיר את השורה הבאה, שלוקחת את הכתובת מקובץ ה-env של הפרויקט
 * ונופלת לכתובת הפרודקשן אם המשתנה לא הוגדר.
 */
export const BASE_URL =
  process.env.VUE_APP_API_BASE_URL

/* ==================================================================
 * קבוצות ונתיבי הבסיס שלהן (קבועים – לא ניתנים לשינוי מהממשק)
 * ================================================================== */
export const API_GROUPS = [
  {
    id: 'crud',
    basePath: '/crud',
    title: 'CRUD – רשומות',
    subtitle: 'יצירה, קריאה, עדכון ומחיקה של שורות בטבלה',
    icon: 'mdi-database-edit-outline',
  },
  {
    id: 'syncCrud',
    basePath: '/queued-crud',
    title: 'CRUD מסונכרן',
    subtitle: 'פעולה אטומית עם נעילה – למניעת התנגשויות',
    icon: 'mdi-sync-circle',
  },
  {
    id: 'table',
    basePath: '/table',
    title: 'טבלאות ושדות',
    subtitle: 'יצירת טבלה, שליפת מבנה ועדכון מאפיינים',
    icon: 'mdi-table-cog',
  },
  {
    id: 'utility',
    basePath: '/utility',
    title: 'כלי עזר',
    subtitle: 'חישובים, תאריכים, מחרוזות, טלפונים ומספרים',
    icon: 'mdi-tools',
  },
]

export const UTILITY_SECTIONS = [
  { id: 'operators', title: 'מחשבון', icon: 'mdi-calculator-variant-outline' },
  { id: 'dates', title: 'תאריכים ושעות', icon: 'mdi-calendar-clock' },
  { id: 'tools', title: 'אימות ונרמול', icon: 'mdi-shield-check-outline' },
  { id: 'strings', title: 'מחרוזות', icon: 'mdi-format-text' },
  { id: 'numbers', title: 'מספרים', icon: 'mdi-numeric' },
]

/* שם הפרמטר של סביבת העבודה כפי שהשרת מצפה לו */
export const WORKSPACE_KEY = 'workspaceId'

/* ==================================================================
 * רשימות בחירה
 * ================================================================== */
export const OPERATOR_OPTIONS = [
  { text: 'שווה ל (==)', value: '==' },
  { text: 'שונה מ (!=)', value: '!=' },
  { text: 'מכיל (contains)', value: 'contains' },
  { text: 'גדול מ (>)', value: '>' },
  { text: 'קטן מ (<)', value: '<' },
  { text: 'גדול או שווה (>=)', value: '>=' },
  { text: 'קטן או שווה (<=)', value: '<=' },
]

export const UPDATE_TYPE_OPTIONS = [
  { text: 'קביעת ערך (set)', value: 'set' },
  { text: 'הוספה למספר (increment)', value: 'increment' },
  { text: 'הפחתה ממספר (decrement)', value: 'decrement' },
  { text: 'הוספה בסוף הטקסט (append)', value: 'append' },
  { text: 'הוספה בתחילת הטקסט (prepend)', value: 'prepend' },
]

export const MATH_OPERATOR_OPTIONS = [
  { text: 'חיבור (+)', value: '+' },
  { text: 'חיסור (-)', value: '-' },
  { text: 'כפל (*)', value: '*' },
  { text: 'חילוק (/)', value: '/' },
]

/* ==================================================================
 * פרמטרים חוזרים
 * ------------------------------------------------------------------
 * advanced: true  →  הפרמטר מוסתר מאחורי "אפשרויות מתקדמות"
 * ================================================================== */
const defaultConditions = () => [{ key: '', operator: '==', value: '' }]
const defaultFieldsToUpdate = () => [{ key: '', value: '', update_type: 'set' }]

const pTableName = (where = 'body') => ({
  name: 'tableName',
  label: 'שם הטבלה',
  in: where,
  type: 'string',
  required: true,
  hint: 'שם הטבלה המערכתי, למשל table32922',
  isTableName: true,
})

const pConditions = () => ({
  name: 'conditions',
  label: 'תנאי סינון',
  in: 'body',
  type: 'conditions',
  required: true,
  default: defaultConditions(),
  hint: 'לפחות תנאי אחד. אפשר להוסיף קבוצת תנאים עם לוגיקה נפרדת',
})

const pLogic = () => ({
  name: 'logic',
  label: 'לוגיקה בין התנאים',
  in: 'body',
  type: 'select',
  required: false,
  default: 'and',
  options: [
    { text: 'וגם (and)', value: 'and' },
    { text: 'או (or)', value: 'or' },
  ],
})

const pSelect = () => ({
  name: 'select',
  label: 'שדות להחזרה (select)',
  in: 'body',
  type: 'stringArray',
  required: false,
  advanced: true,
  fieldSuggest: true,
  hint: 'ריק = כל השדות',
})

const pJoin = (withJoinSelect) => ({
  name: 'join',
  label: 'חיבור לטבלה נוספת (join)',
  in: 'body',
  type: 'json',
  required: false,
  advanced: true,
  hint: 'שאירו ריק אם לא נדרש חיבור',
  template: withJoinSelect
    ? { tableName: '', localKey: 'id', foreignKey: '', joinSelect: [] }
    : { tableName: '', localKey: 'id', foreignKey: '' },
})

const pSort = (where = 'body') => ([
  {
    name: 'sortBy',
    label: 'מיון לפי שדה',
    in: where,
    type: 'string',
    required: false,
    advanced: true,
    fieldSuggest: true,
  },
  {
    name: 'sortDesc',
    label: 'מיון יורד',
    in: where,
    type: 'boolean',
    required: false,
    advanced: true,
    default: false,
    hint: 'מהחדש לישן',
  },
])

const pPaging = (where = 'body') => ([
  { name: 'page', label: 'מספר עמוד', in: where, type: 'number', required: false, advanced: true, default: 1 },
  {
    name: 'itemsPerPage',
    label: 'שורות בעמוד',
    in: where,
    type: 'number',
    required: false,
    advanced: true,
    default: 100,
    hint: 'מקסימום 1000',
  },
])

/* ==================================================================
 * קטלוג הפעולות
 * ================================================================== */
export const ENDPOINTS = [
  /* ------------------------- CRUD – יצירה ------------------------- */
  {
    id: 'create_row',
    group: 'crud',
    method: 'POST',
    path: '/create_row',
    title: 'הוספת רשומה',
    description: 'מוסיף שורה בודדת לטבלה. הערכים עוברים ולידציה מול הגדרות השדות של הטבלה.',
    permission: 'write',
    params: [
      pTableName(),
      {
        name: 'new_row',
        label: 'השורה החדשה',
        in: 'body',
        type: 'keyValue',
        required: true,
        hint: 'שם השדה חייב להתאים לשם השדה המערכתי בטבלה',
      },
    ],
    returns: '{ status: 0, message: "ok", new_row: {...} }',
  },
  {
    id: 'create_rows',
    group: 'crud',
    method: 'POST',
    path: '/create_rows',
    title: 'הוספת מספר רשומות',
    description: 'הוספת אצווה של שורות בקריאה אחת.',
    permission: 'write',
    params: [
      pTableName(),
      {
        name: 'new_rows',
        label: 'מערך השורות',
        in: 'body',
        type: 'json',
        required: true,
        template: [{ TS_name: 'moshe' }, { TS_name: 'david' }],
      },
    ],
    returns: '{ status: 0, message: "ok", new_rows: [...] }',
  },

  /* ------------------------- CRUD – קריאה ------------------------- */
  {
    id: 'get_row',
    group: 'crud',
    method: 'POST',
    path: '/get_row',
    title: 'קבלת רשומה',
    description: 'מחזיר את השורה הראשונה שעונה על התנאים.',
    params: [
      pTableName(),
      pConditions(),
      pLogic(),
      pSelect(),
      pJoin(false),
      {
        name: 'index',
        label: 'אינדקס התוצאה',
        in: 'body',
        type: 'number',
        required: false,
        advanced: true,
        hint: 'התשובה תכלול גם nextIndex – נוח למעבר בין תוצאות בלולאה',
      },
      {
        name: 'random',
        label: 'שורה אקראית',
        in: 'body',
        type: 'boolean',
        required: false,
        advanced: true,
        hint: 'לא ניתן יחד עם אינדקס',
      },
      {
        name: 'returnAsArray',
        label: 'החזרת השדות כמערך',
        in: 'body',
        type: 'boolean',
        required: false,
        advanced: true,
      },
      ...pSort(),
    ],
    returns: '{ status: 0, data: {...} }  |  { status: 4, message: "not found" }',
    notes: ['status 4 מציין שלא נמצאה שורה תואמת – הבקשה עצמה הצליחה וחוזרת עם קוד 200'],
  },
  {
    id: 'get_rows',
    group: 'crud',
    method: 'POST',
    path: '/get_rows',
    title: 'קבלת רשומות',
    description: 'מחזיר את כל השורות שעונות על התנאים, עם עימוד ומיון.',
    params: [
      pTableName(),
      pConditions(),
      pLogic(),
      pSelect(),
      pJoin(true),
      ...pSort(),
      {
        name: 'countNumOnly',
        label: 'ספירה בלבד',
        in: 'body',
        type: 'boolean',
        required: false,
        advanced: true,
        default: false,
        hint: 'מחזיר רק rowsCount בלי הנתונים',
      },
      ...pPaging(),
    ],
    returns: '{ status: 0, data: [...], pageCount, rowsCount }',
  },
  {
    id: 'get_table_rows',
    group: 'crud',
    method: 'GET',
    path: '/get_table_rows',
    title: 'קבלת כל הרשומות בטבלה',
    description: 'מחזיר את כל השורות בטבלה, ללא תנאי סינון.',
    params: [
      pTableName('query'),
      ...pSort('query'),
      ...pPaging('query'),
    ],
    returns: '{ status: 0, data: [...], pageCount, rowsCount }',
  },
  {
    id: 'get_table_rows_count',
    group: 'crud',
    method: 'GET',
    path: '/get_table_rows_count',
    title: 'קבלת מספר הרשומות בטבלה',
    description: 'מחזיר את מספר השורות הכולל – בלי להוריד את הנתונים עצמם.',
    params: [pTableName('query')],
    returns: '{ status: 0, dataRowsCount: 128 }',
  },
  {
    id: 'get_field_value',
    group: 'crud',
    method: 'GET',
    path: '/get_field_value',
    title: 'קבלת ערך שדה מרשומה',
    description: 'מחזיר את הערך הגולמי של שדה אחד בשורה מזוהה. התשובה היא טקסט נקי ולא JSON – שימושי במיוחד למערכות IVR.',
    params: [
      pTableName('query'),
      { name: 'rowId', label: 'מזהה השורה (id)', in: 'query', type: 'string', required: true },
      { name: 'fieldName', label: 'שם השדה', in: 'query', type: 'string', required: true, fieldSuggest: true },
    ],
    returns: 'טקסט גולמי',
  },
  {
    id: 'get_row_short_last_value',
    group: 'crud',
    method: 'POST',
    path: '/get_row_short_last_value',
    title: 'קבלת רשומה לפי ערך מקוצר',
    description: 'מאתר שורה שבה התווים האחרונים של שדה מסוים תואמים לערך שנשלח. מתאים לזיהוי לפי 4 ספרות אחרונות של טלפון.',
    params: [
      pTableName(),
      { name: 'key', label: 'שם השדה לחיפוש', in: 'body', type: 'string', required: true, fieldSuggest: true },
      { name: 'short_value', label: 'הערך המקוצר', in: 'body', type: 'string', required: true, placeholder: '8100' },
      { name: 'num_of_last_charts', label: 'מספר תווים אחרונים', in: 'body', type: 'string', required: true, default: '4' },
    ],
    returns: '{ status: 0, data: {...} }  |  { status: 3, message: "not found" }',
  },

  /* ------------------------- CRUD – עדכון ------------------------- */
  {
    id: 'update_row',
    group: 'crud',
    method: 'POST',
    path: '/update_row',
    title: 'עדכון רשומה',
    description: 'מעדכן את השורה הראשונה שעונה על התנאים. תומך בעדכון יחסי ולא רק בקביעת ערך.',
    permission: 'write',
    params: [
      pTableName(),
      pConditions(),
      pLogic(),
      {
        name: 'fields_to_update',
        label: 'שדות לעדכון',
        in: 'body',
        type: 'fieldsToUpdate',
        required: true,
        default: defaultFieldsToUpdate(),
      },
      {
        name: 'return_document',
        label: 'איזו גרסה להחזיר',
        in: 'body',
        type: 'select',
        required: false,
        advanced: true,
        default: '',
        options: [
          { text: 'ברירת מחדל – אחרי העדכון', value: '' },
          { text: 'אחרי העדכון (after)', value: 'after' },
          { text: 'לפני העדכון (before)', value: 'before' },
          { text: 'שתיהן (both)', value: 'both' },
        ],
      },
    ],
    returns: '{ status: 0, message: "ok", item: {...} }',
    notes: ['העדכון מפעיל אוטומטית אינטגרציות CRM שמוגדרות על הטריגר "כאשר שורה מתעדכנת"'],
  },
  {
    id: 'update_rows',
    group: 'crud',
    method: 'POST',
    path: '/update_rows',
    title: 'עדכון רשומות',
    description: 'מעדכן את כל השורות שעונות על התנאים בבת אחת.',
    permission: 'write',
    params: [
      pTableName(),
      pConditions(),
      pLogic(),
      {
        name: 'fields_to_update',
        label: 'שדות לעדכון',
        in: 'body',
        type: 'fieldsToUpdate',
        required: true,
        default: defaultFieldsToUpdate(),
      },
    ],
    returns: '{ status: 0, message: "ok" }',
  },
  {
    id: 'calc_price_qty_update',
    group: 'crud',
    method: 'POST',
    path: '/calc_price_qty_update',
    title: 'חישוב סכום כנגד כמות ועדכון תוצאה',
    description: 'מבצע חישוב בין שני ערכים ושומר את התוצאה בשדה בטבלה – בקריאה אחת.',
    permission: 'write',
    params: [
      { name: 'price', label: 'ערך ראשון', in: 'body', type: 'string', required: true, placeholder: '600' },
      { name: 'qty', label: 'ערך שני', in: 'body', type: 'string', required: true, placeholder: '2' },
      { name: 'operator', label: 'פעולה', in: 'body', type: 'select', required: true, options: MATH_OPERATOR_OPTIONS, default: '*' },
      { name: 'table_update', label: 'טבלת היעד', in: 'body', type: 'string', required: true, isTableName: true },
      { name: 'where_key_update', label: 'שדה לאיתור השורה', in: 'body', type: 'string', required: true, fieldSuggest: true },
      { name: 'where_value_update', label: 'ערך לאיתור השורה', in: 'body', type: 'string', required: true },
      { name: 'key_to_update', label: 'השדה שיקבל את התוצאה', in: 'body', type: 'string', required: true, fieldSuggest: true },
    ],
    returns: '{ status: 0, details: {...} }',
  },
  {
    id: 'calc_price_qty_get_update',
    group: 'crud',
    method: 'POST',
    path: '/calc_price_qty_get_update',
    title: 'שליפת ערך, חישוב ועדכון תוצאה',
    description: 'שולף ערך משורה אחת, מחשב אותו מול כמות, ושומר את התוצאה בשורה אחרת.',
    permission: 'write',
    params: [
      { name: 'qty', label: 'כמות', in: 'body', type: 'string', required: true, placeholder: '3' },
      { name: 'operator', label: 'פעולה', in: 'body', type: 'select', required: true, options: MATH_OPERATOR_OPTIONS, default: '*' },
      { name: 'table_get', label: 'טבלת המקור', in: 'body', type: 'string', required: true, isTableName: true },
      { name: 'where_key_get', label: 'שדה לאיתור במקור', in: 'body', type: 'string', required: true },
      { name: 'where_value_get', label: 'ערך לאיתור במקור', in: 'body', type: 'string', required: true },
      { name: 'key_to_get', label: 'השדה שממנו נשלף הערך', in: 'body', type: 'string', required: true },
      { name: 'table_update', label: 'טבלת היעד', in: 'body', type: 'string', required: true },
      { name: 'where_key_update', label: 'שדה לאיתור ביעד', in: 'body', type: 'string', required: true },
      { name: 'where_value_update', label: 'ערך לאיתור ביעד', in: 'body', type: 'string', required: true },
      { name: 'key_to_update', label: 'השדה שיקבל את התוצאה', in: 'body', type: 'string', required: true },
    ],
    returns: '{ status: 0, details: {...} }',
  },

  /* ------------------------- CRUD – מחיקה ------------------------- */
  {
    id: 'delete_row',
    group: 'crud',
    method: 'DELETE',
    path: '/delete_row',
    title: 'מחיקת רשומה',
    description: 'מוחק את השורה הראשונה שעונה על התנאים.',
    permission: 'delete',
    danger: true,
    params: [pTableName(), pConditions(), pLogic()],
    returns: '{ status: 0, message: "ok" }',
  },
  {
    id: 'delete_rows',
    group: 'crud',
    method: 'DELETE',
    path: '/delete_rows',
    title: 'מחיקת רשומות',
    description: 'מוחק את כל השורות שעונות על התנאים. הפעולה אינה הפיכה.',
    permission: 'delete',
    danger: true,
    params: [pTableName(), pConditions(), pLogic()],
    returns: '{ status: 0, message: "ok" }',
  },

  /* ------------------------- CRUD מסונכרן ------------------------- */
  {
    id: 'lookup_for_table_then_crud_sync',
    group: 'syncCrud',
    method: 'POST',
    path: '/lookup_for_table_then_crud_sync',
    title: 'שליפה מטבלת מקור ואז יצירה או עדכון ביעד',
    description:
      'שולף שורות מטבלת מקור, מבצע עליהן חישובים, ויוצר או מעדכן שורה בטבלת יעד – תחת נעילה שמונעת התנגשות בין קריאות מקבילות. מתאים למספור רץ, יתרות וכל מקום שבו שתי קריאות בו-זמנית עלולות לדרוס זו את זו.',
    permission: 'write',
    params: [
      {
        name: 'lookup_table',
        label: 'טבלת המקור',
        in: 'body',
        type: 'json',
        required: true,
        template: { tableName: '', as: 'users', where_key: '', equal_value: '' },
        hint: '"as" הוא הכינוי שבו מפנים לתוצאות בהמשך, למשל users[0].PW_Aqueuenumber. אפשר להוסיף sortBy ו-sortDesc',
      },
      {
        name: 'dest_table',
        label: 'טבלת היעד',
        in: 'body',
        type: 'json',
        required: true,
        template: {
          tableName: '',
          action: 'create',
          where_dest_key: '',
          equal_dest_value: '',
          row: { ZS_name: 'jhon', PW_Aqueuenumber: 'users[0].PW_Aqueuenumber' },
        },
        hint: 'action = create או update. עבור update חובה where_dest_key ו-equal_dest_value',
      },
      {
        name: 'operations',
        label: 'חישובים על התוצאות',
        in: 'body',
        type: 'json',
        required: false,
        advanced: true,
        template: [{ field: 'users[0].PW_Aqueuenumber', operation: '+', value: 1 }],
        hint: 'operation: + - * / או append',
      },
      {
        name: 'action_if_empty',
        label: 'להמשיך גם אם המקור ריק',
        in: 'body',
        type: 'boolean',
        required: false,
        advanced: true,
        default: false,
      },
      {
        name: 'default_row',
        label: 'שורת ברירת מחדל',
        in: 'body',
        type: 'json',
        required: false,
        advanced: true,
        template: { QV_phone: 'Default Name', PU_name: 'Default Value' },
        hint: 'נשמרת כשטבלת המקור ריקה והאפשרות שמעל פעילה',
      },
    ],
    returns: '{ status: 0, message: "ok", new_row: {...} }',
    notes: [
      'לפעולה זו מגבלת קצב נפרדת משאר פעולות ה-CRUD',
      'הנעילה היא ברמת החשבון – קריאות מקבילות ימתינו בתור',
    ],
  },

  /* ------------------------- טבלאות ------------------------- */
  {
    id: 'create_table',
    group: 'table',
    method: 'POST',
    path: '/create_table',
    title: 'יצירת טבלה',
    description: 'יוצר טבלה חדשה בסביבת העבודה.',
    permission: 'write',
    params: [pTableName('query')],
    returns: '{ status: "0", message: "ok", table: {...} }',
    notes: [
      'שם הטבלה נשלח כפרמטר בכתובת ולא בגוף הבקשה',
      'משתמש עם הרשאת טבלאות מוגבלת אינו יכול ליצור טבלאות',
    ],
  },
  {
    id: 'get_table_fields',
    group: 'table',
    method: 'GET',
    path: '/get_table_fields',
    title: 'קבלת שדות הטבלה',
    description: 'מחזיר את רשימת השדות של הטבלה, כולל מזהה, סוג והגדרות. זו הקריאה שמזינה את ההשלמה האוטומטית בסביבה הזו.',
    params: [pTableName('query')],
    returns: '{ status: "0", message: "ok", fields: [...] }',
  },
  {
    id: 'update_table_properties',
    group: 'table',
    method: 'POST',
    path: '/update_table_properties',
    title: 'עדכון נתוני טבלה',
    description: 'הוספה, עדכון או מחיקה של שדות, ושינוי הגדרות התצוגה של הטבלה.',
    permission: 'fields',
    params: [
      {
        name: 'tableName',
        label: 'הטבלה',
        in: 'body',
        type: 'json',
        required: true,
        template: { name: '', hebrew_name: '' },
        hint: 'אובייקט ולא מחרוזת – name הוא השם המערכתי',
      },
      {
        name: 'fields',
        label: 'שינויים בשדות',
        in: 'body',
        type: 'json',
        required: true,
        template: [
          {
            action: 'update',
            name: 'field_name',
            hebrew_name: 'שם לתצוגה',
            allowNull: true,
            unique: false,
            show: true,
            type: 'טקסט',
            list: [],
            linkedList: {},
            coin: '',
            id: '33412280',
          },
        ],
        hint: 'action: add / update / remove. עבור update ו-remove חובה id קיים. linkedList לדוגמה: {tableSource: "table21825", fieldSource: "QV_phone"}',
      },
      {
        name: 'settings',
        label: 'הגדרות התצוגה',
        in: 'body',
        type: 'json',
        required: false,
        advanced: true,
        template: { expended_rows: false, multiple_selection: true, show_copy_icon: true },
      },
    ],
    returns: 'אובייקט הטבלה המעודכן',
    notes: ['מומלץ להריץ קודם "קבלת שדות הטבלה" ולהעתיק משם את מזהי השדות'],
  },

  /* ------------------------- כלי עזר – מחשבון ------------------------- */
  {
    id: 'calc_price_qty',
    group: 'utility',
    section: 'operators',
    method: 'POST',
    path: '/operators/calc_price_qty',
    title: 'חישוב סכום כנגד כמות',
    description: 'מחשב תוצאה בין שני ערכים.',
    params: [
      { name: 'price', label: 'ערך ראשון', in: 'body', type: 'string', required: true },
      { name: 'qty', label: 'ערך שני', in: 'body', type: 'string', required: true },
      { name: 'operator', label: 'פעולה', in: 'body', type: 'select', required: true, options: MATH_OPERATOR_OPTIONS, default: '*' },
      { name: 'round', label: 'עיגול התוצאה', in: 'body', type: 'boolean', required: false, advanced: true },
      { name: 'decimalLength', label: 'ספרות אחרי הנקודה', in: 'body', type: 'number', required: false, advanced: true },
    ],
    returns: '{ status: 0, sum: 1200, isDecimal: false }',
    notes: ['לחישוב אצווה שלחו במקום השדות הבודדים: ops: [{ price, qty, operator }] – התשובה תכלול results ו-errors'],
  },
  {
    id: 'calc_precent',
    group: 'utility',
    section: 'operators',
    method: 'POST',
    path: '/operators/calc_precent',
    title: 'חישוב אחוזים',
    description: 'שליחת אחוז מחזירה את סכום ההנחה והמחיר הסופי. שליחת חלק מחזירה כמה אחוזים הוא מהסכום.',
    params: [
      { name: 'price', label: 'סכום', in: 'body', type: 'string', required: true },
      { name: 'precent', label: 'אחוז', in: 'body', type: 'string', required: false, placeholder: '17' },
      { name: 'part', label: 'חלק מהסכום', in: 'body', type: 'string', required: false, hint: 'חלופה לאחוז' },
    ],
    returns: '{ discount_precent, discount_price }  או  { calculated_percent }',
  },

  /* ------------------------- כלי עזר – תאריכים ------------------------- */
  {
    id: 'get_current_date_time',
    group: 'utility',
    section: 'dates',
    method: 'POST',
    path: '/dates/get_current_date_time',
    title: 'קבלת תאריך ושעה',
    description: 'מחזיר את הזמן הנוכחי בפורמט שנבחר. אפשר לבקש רק תאריך, רק שעה או שניהם.',
    params: [
      {
        name: 'date',
        label: 'פורמט תאריך',
        in: 'body',
        type: 'select',
        required: false,
        default: 'dd/mm/yyyy',
        options: [
          { text: 'ללא תאריך', value: '' },
          { text: 'dd-mm-yyyy', value: 'dd-mm-yyyy' },
          { text: 'yyyy-mm-dd', value: 'yyyy-mm-dd' },
          { text: 'dd/mm/yyyy', value: 'dd/mm/yyyy' },
          { text: 'yyyy/mm/dd', value: 'yyyy/mm/dd' },
          { text: 'dd/mm', value: 'dd/mm' },
          { text: 'dd', value: 'dd' },
          { text: 'mm', value: 'mm' },
          { text: 'yyyy', value: 'yyyy' },
        ],
      },
      {
        name: 'time',
        label: 'פורמט שעה',
        in: 'body',
        type: 'select',
        required: false,
        default: 'hh:mm',
        options: [
          { text: 'ללא שעה', value: '' },
          { text: 'hh:mm', value: 'hh:mm' },
          { text: 'hh:mm:ss', value: 'hh:mm:ss' },
          { text: 'hh:mm:ss:mmm', value: 'hh:mm:ss:mmm' },
        ],
      },
    ],
    returns: '{ status: 0, result: "26/07/2026T14:35" }',
    notes: ['חובה לבחור לפחות אחד מהשניים'],
  },
  {
    id: 'get_current_full_date',
    group: 'utility',
    section: 'dates',
    method: 'GET',
    path: '/dates/get_current_full_date',
    title: 'קבלת תאריך ושעה מלא',
    description: 'מחזיר יום, חודש, שנה, יום בשבוע, שעות, דקות ושניות כשדות נפרדים.',
    params: [
      { name: 'costumeDate', label: 'תאריך לפירוק', in: 'query', type: 'string', required: false, hint: 'ריק = עכשיו' },
    ],
    returns: '{ status: 0, result: { day_in_month, month, year, day_in_week, hours, minutes, seconds, miliSeconds } }',
  },
  {
    id: 'calc_time_between_dates',
    group: 'utility',
    section: 'dates',
    method: 'POST',
    path: '/dates/calc_time_between_dates',
    title: 'חישוב זמן בין תאריכים',
    description: 'מחשב את המרחק בין שני תאריכים ומחזיר אותו בכמה צורות: סך שניות ודקות, פירוק קלנדרי ופורמט שעון.',
    params: [
      { name: 'dateStart', label: 'תאריך התחלה', in: 'body', type: 'string', required: false, hint: 'ריק = עכשיו' },
      { name: 'dateEnd', label: 'תאריך סיום', in: 'body', type: 'string', required: false, hint: 'ריק = עכשיו' },
    ],
    returns: '{ status: 0, isNegative, totals, calendarBreakdown, hhmmss }',
    notes: [
      'חובה לשלוח לפחות אחד מהשניים',
      'נתמכים ISO, dd/mm/yyyy, dd-mm-yyyy ו-yyyy-mm-dd – עם או בלי שעה',
    ],
  },
  {
    id: 'add_offset',
    group: 'utility',
    section: 'dates',
    method: 'POST',
    path: '/dates/add_offset',
    title: 'הוספת או חיסור זמן מתאריך',
    description: 'מוסיף או מחסיר זמן מתאריך בסיס. ערכים שליליים מחסירים. מטפל נכון בסופי חודש ובשעון קיץ.',
    params: [
      { name: 'baseDate', label: 'תאריך בסיס', in: 'body', type: 'string', required: false, hint: 'ריק = עכשיו', placeholder: '2025-01-18' },
      { name: 'days', label: 'ימים', in: 'body', type: 'number', required: false },
      { name: 'hours', label: 'שעות', in: 'body', type: 'number', required: false },
      { name: 'minutes', label: 'דקות', in: 'body', type: 'number', required: false, placeholder: '90' },
      { name: 'seconds', label: 'שניות', in: 'body', type: 'number', required: false, advanced: true },
      { name: 'months', label: 'חודשים', in: 'body', type: 'number', required: false, advanced: true, hint: 'ערך שלילי מחסיר' },
      { name: 'years', label: 'שנים', in: 'body', type: 'number', required: false, advanced: true },
      { name: 'timeZone', label: 'אזור זמן', in: 'body', type: 'string', required: false, advanced: true, placeholder: 'Asia/Jerusalem' },
      { name: 'roundToSecond', label: 'עיגול לשנייה', in: 'body', type: 'boolean', required: false, advanced: true },
    ],
    returns: '{ status: 0, result: { calcLocal, serverLocal, isoUtc } }',
  },

  /* ------------------------- כלי עזר – אימות ונרמול ------------------------- */
  {
    id: 'format_phone_with_prefix',
    group: 'utility',
    section: 'tools',
    method: 'GET',
    path: '/tools/format_phone_with_prefix',
    title: 'הוספת קוד מדינה לטלפון',
    description: 'ממיר מספר לפורמט בינלאומי אחיד. מזהה גם מספרים חסויים ומספרי חו"ל.',
    params: [{ name: 'phone', label: 'מספר טלפון', in: 'query', type: 'string', required: true, placeholder: '542608100' }],
    returns: '{ status: 0, formatedPhone: "972542608100" }',
    notes: ['status 4 מוחזר עם קוד 200 כשהמספר חסוי או לא תקין'],
  },
  {
    id: 'validate_israeli_phone',
    group: 'utility',
    section: 'tools',
    method: 'POST',
    path: '/tools/validate_israeli_phone',
    title: 'אימות טלפון ישראלי',
    description: 'בודק אם המספר הוא מספר ישראלי תקין.',
    params: [{ name: 'phone', label: 'מספר טלפון', in: 'query', type: 'string', required: true, placeholder: '0527000000' }],
    returns: '{ status: 0, ... }',
    notes: ['הפעולה היא POST אך הפרמטר נשלח בכתובת ולא בגוף הבקשה'],
  },
  {
    id: 'validate_kosher_phone',
    group: 'utility',
    section: 'tools',
    method: 'POST',
    path: '/tools/validate_kosher_phone',
    title: 'אימות מספר כשר',
    description: 'בודק אם המספר שייך לקידומת כשרה, ומחזיר גם אם מדובר בנייד או בקו נייח.',
    params: [{ name: 'phone', label: 'מספר טלפון', in: 'query', type: 'string', required: true, placeholder: '0527000000' }],
    returns: '{ status: 0, isKosher: true, phoneType: "mobile" }',
    notes: ['הפעולה היא POST אך הפרמטר נשלח בכתובת ולא בגוף הבקשה'],
  },
  {
    id: 'validate_israeli_id_number',
    group: 'utility',
    section: 'tools',
    method: 'POST',
    path: '/tools/validate_israeli_id_number',
    title: 'אימות תעודת זהות',
    description: 'מאמת ספרת ביקורת של תעודת זהות ישראלית.',
    params: [
      { name: 'idNumber', label: 'מספר תעודת זהות', in: 'query', type: 'string', required: true, placeholder: '17878737' },
      {
        name: 'padWithLeadingZeros',
        label: 'השלמת אפסים מובילים',
        in: 'query',
        type: 'boolean',
        required: false,
        advanced: true,
        default: true,
        hint: 'משלים ל-9 ספרות לפני הבדיקה',
      },
    ],
    returns: '{ status: 0, isValid: true }',
    notes: ['הפעולה היא POST אך הפרמטרים נשלחים בכתובת ולא בגוף הבקשה'],
  },
  {
    id: 'validate_data_type',
    group: 'utility',
    section: 'tools',
    method: 'POST',
    path: '/tools/validate_data_type',
    title: 'בדיקת סוג מידע',
    description: 'בודק אם ערך מורכב רק מאותיות, רק ממספרים, או שהוא תאריך תקין.',
    params: [
      { name: 'data', label: 'הערך לבדיקה', in: 'body', type: 'string', required: true },
      {
        name: 'type',
        label: 'סוג לבדיקה',
        in: 'body',
        type: 'select',
        required: true,
        default: 'numbers',
        options: [
          { text: 'אותיות בלבד', value: 'letters' },
          { text: 'מספרים בלבד', value: 'numbers' },
          { text: 'תאריך', value: 'date' },
        ],
      },
    ],
    returns: '{ status: 0, match: true }',
  },
  {
    id: 'create_random_password',
    group: 'utility',
    section: 'tools',
    method: 'POST',
    path: '/tools/create_random_password',
    title: 'יצירת סיסמה רנדומלית',
    description: 'מייצר מחרוזת אקראית לפי האורך וסוגי התווים שנבחרו.',
    params: [
      { name: 'length', label: 'אורך', in: 'body', type: 'number', required: true, default: 8 },
      { name: 'includeNumbers', label: 'ספרות', in: 'body', type: 'boolean', required: true, default: true },
      { name: 'includeUppercase', label: 'אותיות גדולות', in: 'body', type: 'boolean', required: true, default: true },
      { name: 'includeLowercase', label: 'אותיות קטנות', in: 'body', type: 'boolean', required: true, default: true },
      { name: 'includeSymbols', label: 'תווים מיוחדים', in: 'body', type: 'boolean', required: true, default: false },
    ],
    returns: '{ status: 0, randomPassword: "aB3xK9zQ" }',
    notes: ['ארבעת שדות הבחירה נשלחים תמיד – גם כשהערך שלהם שלילי'],
  },

  /* ------------------------- כלי עזר – מחרוזות ------------------------- */
  {
    id: 'substring',
    group: 'utility',
    section: 'strings',
    method: 'POST',
    path: '/strings/substring',
    title: 'חיתוך מחרוזת',
    description: 'מחזיר חלק מהטקסט. המיקומים יכולים להיות מספרים או מחרוזות – ואז החיתוך מתבצע לפי מיקום המחרוזת בטקסט.',
    params: [
      { name: 'text', label: 'הטקסט', in: 'body', type: 'string', required: true },
      { name: 'indexStart', label: 'התחלה', in: 'body', type: 'string', required: true, default: '0' },
      { name: 'indexEnd', label: 'סיום', in: 'body', type: 'string', required: true, default: '5' },
      { name: 'reverse', label: 'החזרה בסדר הפוך', in: 'body', type: 'boolean', required: false, advanced: true, default: false },
    ],
    returns: '{ status: 0, formatedString: "..." }',
  },
  {
    id: 'split',
    group: 'utility',
    section: 'strings',
    method: 'POST',
    path: '/strings/split',
    title: 'פיצול מחרוזת',
    description: 'מפצל מחרוזת לפי תו או מילה מפרידים.',
    params: [
      { name: 'text', label: 'הטקסט', in: 'body', type: 'string', required: true },
      { name: 'splitBy', label: 'מפריד', in: 'body', type: 'string', required: true, default: ',' },
    ],
    returns: '{ status: 0, splitedArray: [...] }',
  },
  {
    id: 'find_and_replace_text',
    group: 'utility',
    section: 'strings',
    method: 'POST',
    path: '/strings/find_and_replace_text',
    title: 'חיפוש מילה בטקסט והחלפה',
    description: 'מחליף את כל המופעים של ערך בטקסט.',
    params: [
      { name: 'text', label: 'הטקסט', in: 'body', type: 'string', required: true },
      { name: 'find', label: 'לחפש', in: 'body', type: 'string', required: true },
      { name: 'replace', label: 'להחליף ב', in: 'body', type: 'string', required: true, hint: 'ערך ריק ימחק את הטקסט שנמצא' },
    ],
    returns: '{ status: 0, replacedText: "..." }',
  },
  {
    id: 'count_words_or_characters',
    group: 'utility',
    section: 'strings',
    method: 'POST',
    path: '/strings/count_words_or_characters',
    title: 'ספירת מילים או תווים',
    description: 'מחזיר את מספר המילים או התווים בטקסט.',
    params: [
      { name: 'text', label: 'הטקסט', in: 'body', type: 'string', required: true },
      {
        name: 'countType',
        label: 'מה לספור',
        in: 'body',
        type: 'select',
        required: true,
        default: 'words',
        options: [
          { text: 'מילים', value: 'words' },
          { text: 'תווים', value: 'characters' },
        ],
      },
    ],
    returns: '{ status: 0, count: 12 }',
  },
  {
    id: 'match',
    group: 'utility',
    section: 'strings',
    method: 'POST',
    path: '/strings/match',
    title: 'בדיקת ערך במחרוזת',
    description: 'בודק אם הטקסט מתחיל, מסתיים או מכיל ערך מסוים.',
    params: [
      { name: 'text', label: 'הטקסט', in: 'body', type: 'string', required: true },
      { name: 'substring', label: 'הערך לבדיקה', in: 'body', type: 'string', required: true },
      {
        name: 'type',
        label: 'סוג הבדיקה',
        in: 'body',
        type: 'select',
        required: true,
        default: 'includes',
        options: [
          { text: 'מתחיל ב', value: 'startWith' },
          { text: 'מסתיים ב', value: 'endWith' },
          { text: 'מכיל', value: 'includes' },
        ],
      },
    ],
    returns: '{ status: 0, result: true }',
  },

  /* ------------------------- כלי עזר – מספרים ------------------------- */
  {
    id: 'generate_sequence',
    group: 'utility',
    section: 'numbers',
    method: 'POST',
    path: '/numbers/generate_sequence',
    title: 'קבלת רצף מספרים',
    description: 'מייצר רצף מספרים עוקבים מנקודת התחלה נתונה.',
    params: [
      {
        name: 'type',
        label: 'פורמט החזרה',
        in: 'body',
        type: 'select',
        required: true,
        default: 'array',
        options: [
          { text: 'מערך', value: 'array' },
          { text: 'מחרוזת מופרדת בפסיקים', value: 'string' },
        ],
      },
      {
        name: 'data',
        label: 'הגדרות הסדרה',
        in: 'body',
        type: 'json',
        required: true,
        template: { start: 45, quantity: 3 },
      },
    ],
    returns: '{ status: 0, sequence: [45,46,47] }',
  },
]

/* ==================================================================
 * קביעה אוטומטית של מיקום ה-workspaceId
 * ------------------------------------------------------------------
 * פעולות כלי העזר אינן קשורות לסביבת עבודה ולכן אינן מקבלות אותו.
 * בשאר הפעולות הוא נוסע יחד עם שאר הפרמטרים: בגוף הבקשה, ובפעולות
 * שכל הפרמטרים שלהן בכתובת – גם הוא בכתובת.
 * ================================================================== */
ENDPOINTS.forEach(endpoint => {
  if (endpoint.workspaceIn !== undefined) return

  if (endpoint.group === 'utility') {
    endpoint.workspaceIn = null
    return
  }

  const allInQuery = endpoint.params.length > 0 && endpoint.params.every(p => p.in === 'query')
  endpoint.workspaceIn = allInQuery ? 'query' : 'body'
})

/* ==================================================================
 * עזרים
 * ================================================================== */
export const METHOD_COLORS = {
  GET: '#2e7d32',
  POST: '#1565c0',
  PUT: '#ef6c00',
  DELETE: '#c62828',
}

export const STATUS_LEGEND = [
  { code: 0, text: 'הפעולה הצליחה', color: 'success' },
  { code: 1, text: 'שגיאה כללית או חוסר הרשאה', color: 'error' },
  { code: 2, text: 'שגיאה פנימית בשרת', color: 'error' },
  { code: 3, text: 'פרמטר חסר או לא תקין', color: 'warning' },
  { code: 4, text: 'לא נמצאו נתונים', color: 'grey' },
]

export function findEndpoint(id) {
  return ENDPOINTS.find(e => e.id === id) || null
}

/** הכתובת המלאה של פעולה, כולל נתיב הבסיס של הקבוצה */
export function endpointUrl(endpoint) {
  if (!endpoint) return ''
  const group = API_GROUPS.find(g => g.id === endpoint.group)
  const base = String(BASE_URL).replace(/\/+$/, '')
  return base + (group ? group.basePath : '') + endpoint.path
}

/** ערכי ברירת מחדל לטופס של פעולה מסוימת */
export function buildDefaultValues(endpoint) {
  const values = {}
  if (!endpoint) return values

  endpoint.params.forEach(p => {
    if (p.default !== undefined) {
      values[p.name] = JSON.parse(JSON.stringify(p.default))
    } else if (p.type === 'conditions') {
      values[p.name] = defaultConditions()
    } else if (p.type === 'fieldsToUpdate') {
      values[p.name] = defaultFieldsToUpdate()
    } else if (p.type === 'stringArray') {
      values[p.name] = []
    } else if (p.type === 'keyValue') {
      values[p.name] = [{ key: '', value: '' }]
    } else if (p.type === 'json') {
      // תבנית מוכנה רק לשדות חובה. שדה אופציונלי מתחיל ריק ולא נשלח,
      // ואפשר לטעון לו את התבנית בלחיצה על "תבנית".
      values[p.name] = p.required && p.template !== undefined ? JSON.stringify(p.template, null, 2) : ''
    } else if (p.type === 'boolean') {
      values[p.name] = false
    } else {
      values[p.name] = ''
    }
  })

  return values
}